import React from 'react';
import { Link } from 'react-router-dom';
import { formatDistanceToNow } from 'date-fns';
import {
  DocumentTextIcon,
  VideoCameraIcon,
  CheckCircleIcon,
  XCircleIcon,
  ClockIcon,
  PlusCircleIcon
} from '@heroicons/react/24/outline';
import { formatDuration } from '../../../utils/formatDuration';

const ActivityFeed = ({ activities = [], maxItems = 10 }) => {
  const getActivityIcon = (type) => {
    const iconClasses = 'h-5 w-5';
    switch (type) {
      case 'session_created':
        return <PlusCircleIcon className={`${iconClasses} text-blue-500`} />;
      case 'transcript_completed':
        return <CheckCircleIcon className={`${iconClasses} text-green-500`} />;
      case 'transcript_failed':
        return <XCircleIcon className={`${iconClasses} text-red-500`} />;
      case 'file_uploaded':
        return <DocumentTextIcon className={`${iconClasses} text-purple-500`} />;
      case 'session_updated':
        return <ClockIcon className={`${iconClasses} text-orange-500`} />;
      default:
        return <DocumentTextIcon className={`${iconClasses} text-gray-500`} />;
    }
  };

  const getActivityColor = (type) => {
    switch (type) {
      case 'session_created':
        return 'bg-blue-50 border-blue-200';
      case 'transcript_completed':
        return 'bg-green-50 border-green-200';
      case 'transcript_failed':
        return 'bg-red-50 border-red-200';
      case 'file_uploaded':
        return 'bg-purple-50 border-purple-200';
      case 'session_updated':
        return 'bg-orange-50 border-orange-200';
      default:
        return 'bg-gray-50 border-gray-200';
    }
  };

  const displayedActivities = activities.slice(0, maxItems);

  if (displayedActivities.length === 0) {
    return (
      <div>
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Recent Activity
        </h3>
        <div className="text-center py-12">
          <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-3" />
          <p className="text-gray-500">No recent activity</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-900 mb-4">
        Recent Activity
      </h3>
      <div className="space-y-3">
        {displayedActivities.map((activity, index) => (
          <div
            key={activity.id || index}
            className={`border rounded-lg p-3 ${getActivityColor(activity.type)} transition-colors hover:shadow-sm`}
          >
            <div className="flex items-start gap-3">
              <div className="flex-shrink-0 mt-0.5">
                {getActivityIcon(activity.type)}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-900">
                  {activity.title || activity.message || activity.description}
                </p>
                {activity.description && activity.title && (
                  <p className="text-xs text-gray-600">
                    {activity.description}
                  </p>
                )}
                <div className="flex items-center justify-between mt-2">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">
                      {activity.timestamp
                        ? formatDistanceToNow(new Date(activity.timestamp), { addSuffix: true })
                        : 'Just now'}
                    </span>
                    {activity.duration_seconds && (
                      <span className="text-xs text-gray-500">
                        • {formatDuration(activity.duration_seconds)}
                      </span>
                    )}
                  </div>
                  {activity.link && (
                    <Link
                      to={activity.link}
                      className="text-xs text-blue-600"
                    >
                      View →
                    </Link>
                  )}
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>
      {activities.length > maxItems && (
        <div className="mt-4 text-center">
          <Link
            to="/sessions"
            className="text-sm text-blue-600"
          >
            View all activity →
          </Link>
        </div>
      )}
    </div>
  );
};

export default ActivityFeed;
